// WHAT IDENTITY DOES A RENAME'S DESTINATION HAVE? — one answer, derived here,
// and CARRIED from here on.
//
// Five surfaces have to name the destination:
//
//   • the SCHEMA ALTER (`renameSchemaAlter.ts` → `synthesizeAlterRenameColumn`
//     / the per-engine table+view emitters) — which decides what the catalog
//     ends up holding;
//   • the BODY rewrites (`renameObjectBodyEdits.ts` → `terminalTokenEdit.ts`) —
//     which have to name exactly that;
//   • the OPEN-TAB rewrites (`renamePostApplyTabPass.ts` → `tabRewritePlanner`)
//     — which match live buffers against it;
//   • the UNDO (`renameService.makeInverseBatch`) — which has to name the
//     ORIGINAL spelling back;
//   • and the undo's own tab pass, which runs the third one again in reverse.
//
// They used to answer it separately, each by PREDICTING the catalog's spelling
// from whatever identifier it happened to hold. Three ways that drifted:
//
//  1. A destination the user typed WITH delimiters (`"MixedCase"`,
//     `[MixedCase]`, `` `MixedCase` ``) reached the synthesizer still wearing
//     them, so `maybeQuoteIdent` wrapped it AGAIN — `TO """MixedCase"""`
//     creates a column whose NAME contains quote characters — while the
//     rewrites took the typed spelling verbatim. The delimiters come off
//     exactly once now, at the rename entry points
//     ({@link typedDestinationIdentifier}), and the fact that the user meant
//     them rides on the identifier's `quoting` field from there on.
//
//  2. The batch retained the CURSOR-derived destination, not the ALTER's.
//     PostgreSQL's `ALTER TABLE users RENAME COLUMN id TO User_Id` creates
//     `user_id`; a batch that kept `User_Id` made `makeInverseBatch` hand undo
//     a name no buffer holds, so undo restored the database column and left
//     every open tab on `user_id`.
//
//  3. The UNDO direction predicted too. `makeInverseBatch` swapped two
//     identifiers and let the tab pass re-run the prediction on
//     `target.fromIdentifier` — an identity that carries `quoted: true` but no
//     `quoting`, while the predictor read only `quoting`. A case-EXACT original
//     therefore folded on the way back (PostgreSQL `Id` → `id`, Oracle `row_id`
//     → `ROW_ID`) while the undo ALTER, which asks a different question, put
//     the exact bytes back. The buffer then named a column that does not exist.
//
// The prediction ({@link destinationCatalogIdentity}) is MODULE-PRIVATE for
// that reason: a `RenameDestination` can only be produced here, by
// {@link resolveRenameDestination} (forward), {@link resolveRestoreDestination}
// (undo) or {@link catalogDestination} (a spelling the catalog already holds,
// which predicts nothing). No other surface is in a position to disagree.

import type { IdentifierQuoting, QualifiedName, SqlEngine } from '../../sql-grammar/model';
import {
  ENGINE_DEFAULT_QUOTING,
  foldAsStored,
  needsDelimitersToNameExactly,
  needsQuoting,
  decodeDelimitedIdentifier,
} from '../../sql-resolver/ddl/apply/dialect.quoting';
import { foldIdentifier } from '../../sql-resolver/scope/identifiers';
import type { RenameBatch, RenameDestination, RenameKind, RenameRequest } from '../types';

export type { RenameDestination };

/**
 * The destination identity a rename ENTRY POINT builds from the text the user
 * typed: Monaco's F2 box (`renameProvider.ts`) and the Object Explorer's rename
 * input (`renamePreviewStore.ts`).
 *
 * The typed delimiters come off here, once, and survive as `quoting`. `quoted`
 * goes true with them (a delimited name never folds), and otherwise keeps
 * following the source identifier as it always did. `raw` keeps the spelling
 * the user actually typed so a surface that reports the request still can.
 */
export function typedDestinationIdentifier(
  from: QualifiedName,
  typed: string,
  engine: SqlEngine | null,
): QualifiedName {
  const decoded = engine
    ? decodeDelimitedIdentifier(typed, engine)
    : { name: typed, quoting: 'unquoted' as const };
  const delimited = decoded.quoting !== 'unquoted';
  const quoted = delimited || from.quoted;
  // Fold the new name exactly as the engine folds an unquoted/quoted identifier
  // (Oracle → UPPER, postgres → lower, others → lower; quoted names preserve
  // case). F42: this was once a hardcoded `.toLowerCase()`, which emitted an
  // engine-incorrect foldedName for Oracle.
  const foldedName = engine
    ? foldIdentifier(decoded.name, quoted, { engine })
    : decoded.name.toLowerCase();
  return {
    name: decoded.name,
    schema: from.schema,
    database: from.database,
    raw: [from.database, from.schema, typed].filter(Boolean).join('.'),
    quoted,
    ...(delimited ? { quoting: decoded.quoting } : {}),
    foldedName,
    foldedSchema: from.foldedSchema,
    foldedDatabase: from.foldedDatabase,
  };
}

/**
 * The FORWARD destination for one plan — decoded name, quoting intent, and the
 * catalog identity the schema ALTER will create.
 *
 * The decode is idempotent by construction: an identifier that already carries
 * a `quoting` intent was decoded at the entry point and is taken as-is; one
 * that does not is decoded here. That second pass is not belt-and-braces — the
 * planner is the single funnel every caller reaches (the two UI entry points,
 * the command palette, the agent/MCP surface, tests), and it is what makes "the
 * ALTER never wraps a delimiter twice" an invariant of the plan rather than a
 * habit of two call sites.
 */
export function resolveRenameDestination(
  request: RenameRequest,
  engine: SqlEngine,
): RenameDestination {
  const to = request.toIdentifier;
  const decoded =
    to.quoting !== undefined && to.quoting !== 'unquoted'
      ? { name: to.name, quoting: to.quoting }
      : decodeDelimitedIdentifier(to.name, engine);
  return buildDestination(to, decoded.name, decoded.quoting, request, engine);
}

/**
 * The destination the plan's UNDO restores — the ORIGINAL object's catalog
 * identity, derived at plan time from exactly the statement
 * `renameSchemaAlter.ts` emits as `undoBatch[0]`.
 *
 * The one destination in a rename plan that is not a name the user typed. Every
 * other one is, and there the engine's fold IS the intended identity; this one
 * has to reproduce bytes the catalog held BEFORE the plan ran, so it asks for
 * the delimiters that name those bytes exactly ({@link restoreQuoting}) rather
 * than letting the fold decide. `Id` on PostgreSQL and `row_id` on Oracle both
 * need them; `key_id` on either needs none.
 */
export function resolveRestoreDestination(
  request: RenameRequest,
  engine: SqlEngine,
): RenameDestination {
  const from = request.fromIdentifier;
  return buildDestination(from, from.name, restoreQuoting(from.name, engine), request, engine);
}

/**
 * A spelling the catalog ALREADY holds, read as a destination — no prediction
 * of any kind, because the bytes are the answer.
 *
 * `RenameBatch.destination` / `.undoDestination` are REQUIRED, so no TYPED
 * caller can reach {@link destinationOf}'s fallback any more. What still can is
 * the E2E seam: `main.tsx` exposes `applyRename` / `undoLastRename` on
 * `window.__TEST_STORES__.navRename`, and a dozen specs hand it a batch literal
 * written inside `page.evaluate` — untyped by construction, and none of them
 * names a destination. Those are the fallback's only remaining callers, and it
 * reads `target.toIdentifier` / `target.fromIdentifier`, both catalog
 * identities by the batch's own invariant.
 *
 * A destination built here EQUALS the identifier it was built from, so the
 * fallback cannot be the source of a `destination` ≠ `target.toIdentifier`
 * disagreement — it is the one path where the two agree by construction.
 *
 * It is also the fixtures' one-liner: a unit test that does not care what the
 * ALTER spells names its destination with this rather than restating one.
 */
export function catalogDestination(identifier: QualifiedName): RenameDestination {
  return {
    name: identifier.name,
    quoting: identifier.quoting ?? 'unquoted',
    catalogName: identifier.name,
    identifier,
  };
}

/** The destination a batch's `schemaAlter` created. The ONE reader every
 *  post-apply consumer goes through — see `RenameBatch.destination`.
 *
 *  The `??` is not a type-level possibility any more (the field is required);
 *  it is the runtime net under the untyped E2E seam described on
 *  {@link catalogDestination}, which reaches this with a batch literal TypeScript
 *  never saw. */
export function destinationOf(batch: RenameBatch): RenameDestination {
  return batch.destination ?? catalogDestination(batch.target.toIdentifier);
}

/** Assemble one destination: the bytes, the delimiters the ALTER writes around
 *  them, the spelling that leaves in the catalog, and the identity every
 *  post-apply consumer matches against. `qualifiers` supplies the schema /
 *  database components, which a rename never changes. */
function buildDestination(
  qualifiers: QualifiedName,
  name: string,
  quoting: IdentifierQuoting,
  request: RenameRequest,
  engine: SqlEngine,
): RenameDestination {
  const catalog = destinationCatalogIdentity({
    newName: name,
    engine,
    kind: request.kind,
    containingTable: request.containingTable,
    destinationQuoting: quoting,
  });
  return {
    name,
    quoting,
    catalogName: catalog.name,
    identifier: {
      name: catalog.name,
      schema: qualifiers.schema,
      database: qualifiers.database,
      raw: [qualifiers.database, qualifiers.schema, catalog.name].filter(Boolean).join('.'),
      quoted: catalog.caseExact,
      ...(quoting !== 'unquoted' ? { quoting } : {}),
      foldedName: catalogFoldedName(catalog.name, engine),
      foldedSchema: qualifiers.foldedSchema,
      foldedDatabase: qualifiers.foldedDatabase,
    },
  };
}

/** The delimiters an UNDO needs around the name it restores, or `'unquoted'`
 *  when the engine would store that spelling from a bare token anyway.
 *
 *  `quoteNames` mirrors the SOURCE's quoting (DSE6), so a BARE host relation
 *  says "unquoted style" — but an existing catalog column `Id` has no unquoted
 *  spelling that still denotes it: `... RENAME COLUMN key_id TO Id` asks
 *  PostgreSQL for `id` and Oracle for `ID`, columns the server does not have. */
function restoreQuoting(name: string, engine: SqlEngine): IdentifierQuoting {
  return needsDelimitersToNameExactly(name, engine) ? ENGINE_DEFAULT_QUOTING[engine] : 'unquoted';
}

type DestinationCatalogInput = {
  readonly newName: string;
  readonly engine: SqlEngine;
  readonly kind: RenameKind;
  /** The renamed column's host relation — the identifier whose `quoted` flag
   *  `renameSchemaAlter.ts` hands `synthesizeAlterRenameColumn` as its
   *  `quoteNames` argument. Ignored for a table/view rename. */
  readonly containingTable?: QualifiedName;
  /** The delimiters the ALTER writes around the destination. Anything but
   *  `'unquoted'` means the catalog keeps its exact bytes. */
  readonly destinationQuoting: IdentifierQuoting;
};

/**
 * The spelling the CATALOG holds once the schema-side rename has run, and
 * whether those bytes are case-EXACT (the ALTER delimited them, or carried them
 * in a literal) rather than the product of the engine's fold.
 *
 * Read off the statements `renameSchemaAlter.ts` actually emits, because those
 * are what create the name every rewritten body then has to match:
 *
 *   kind         engine                 destination in the ALTER      folds?
 *   ───────────  ─────────────────────  ────────────────────────────  ──────
 *   table/view   all five               delimited (`"x"`/`` `x` ``/   no
 *                                       `[x]`) or an `sp_rename`
 *                                       string literal
 *   column       mssql                  `sp_rename` string literal    no
 *   column       others, delimited      delimited (the owner's style) no
 *   column       others, quoted table   delimited (the owner's style) no
 *   column       others, bare table     BARE                          yes
 *
 * Only the last row folds, and only on the two engines that fold an unquoted
 * identifier on the way into the catalog: Oracle (UPPER) and PostgreSQL
 * (lower). MySQL and SQLite store an unquoted `RENAME COLUMN` target with the
 * case it was typed in, and MSSQL's `sp_rename` carries it as a literal — for
 * those three the catalog spelling IS the typed spelling, so nothing changes.
 * (`foldIdentifier` is deliberately not reused here: its mssql/mysql/sqlite
 * case is the CASE-INSENSITIVE MATCHING fold — lowercase — which is a different
 * question from how the server STORES the name.)
 *
 * A name that cannot stand unquoted (`user id`, `1st`, `select`) never folds
 * either: the only ALTER that can create it is a delimited one.
 *
 * PRIVATE ON PURPOSE. This is the prediction, and every consumer that ran its
 * own copy of it eventually predicted something the ALTER did not do.
 */
function destinationCatalogIdentity(
  input: DestinationCatalogInput,
): { readonly name: string; readonly caseExact: boolean } {
  const { newName, engine, kind, containingTable, destinationQuoting } = input;
  const exact = { name: newName, caseExact: true };
  if (destinationQuoting !== 'unquoted') return exact;
  // Asked OF THIS ENGINE, exactly as `synthesizeAlterRenameColumn` asks it:
  // the two answers are the same decision read from opposite ends, and the
  // engine-less form disagreed for a MIXED-CASE `#` destination on PostgreSQL /
  // MySQL / SQLite (and a leading `_` on Oracle). There the ALTER delimits, so
  // the catalog keeps the exact bytes, while this predicted a fold — and
  // `renderCatalogToken` then wrote the reference bare, a token MySQL reads as
  // the start of a comment.
  if (needsQuoting(newName, engine)) return exact;
  if (kind !== 'column') return exact;
  if (engine === 'mssql') return exact;
  if (containingTable?.quoted === true) return exact;
  return { name: foldAsStored(newName, engine), caseExact: false };
}

/** The MATCHING key for a catalog spelling.
 *
 *  Oracle and PostgreSQL compare identifiers byte-exactly once a reference has
 *  been folded by its own rules, and `catalogName` already IS the stored
 *  spelling — so it is the key, whether the ALTER folded it (`user_id`) or
 *  delimited it (`MixedCase`). The other three fold case-INSENSITIVELY whatever
 *  the catalog holds, which is the lowercase key `foldIdentifier` gives. */
function catalogFoldedName(catalogName: string, engine: SqlEngine): string {
  const storedCaseExact = engine === 'oracle' || engine === 'postgres';
  return foldIdentifier(catalogName, storedCaseExact, { engine });
}
